import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { MudancaEvidencia } from '@/types/mudanca';

export const useMudancaEvidencias = (mudancaId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: evidencias = [], isLoading } = useQuery({
    queryKey: ['mudanca-evidencias', mudancaId],
    queryFn: async () => {
      if (!mudancaId) return [];
      
      const { data, error } = await supabase
        .from('mudanca_evidencias' as any)
        .select('*')
        .eq('mudanca_id', mudancaId)
        .order('criado_em', { ascending: false });

      if (error) throw error;
      return ((data as any[]) || []) as MudancaEvidencia[];
    },
    enabled: !!mudancaId,
  });

  const uploadEvidencia = useMutation({
    mutationFn: async ({ 
      mudancaId, 
      file, 
      tipo, 
      etapaWorkflow 
    }: { 
      mudancaId: string; 
      file: File; 
      tipo: string;
      etapaWorkflow?: string;
    }) => {
      const { data: user } = await supabase.auth.getUser();
      
      // Upload do arquivo para o Storage
      const fileName = `${mudancaId}/${Date.now()}-${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('mudancas')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Obter URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('mudancas')
        .getPublicUrl(fileName);

      // Criar registro na tabela
      const { data, error } = await supabase
        .from('mudanca_evidencias' as any)
        .insert({
          mudanca_id: mudancaId,
          tipo,
          nome_arquivo: file.name,
          url_arquivo: publicUrl,
          tamanho_bytes: file.size,
          mime_type: file.type,
          etapa_workflow: etapaWorkflow,
          criado_por: user.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudanca-evidencias', mudancaId] });
      toast({
        title: "Sucesso",
        description: "Evidência enviada com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao enviar evidência:', error);
      toast({
        title: "Erro",
        description: "Erro ao enviar evidência. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const deleteEvidencia = useMutation({
    mutationFn: async (evidenciaId: string) => {
      // Buscar evidência para obter URL do arquivo
      const { data: evidencia, error: fetchError } = await supabase
        .from('mudanca_evidencias' as any)
        .select('url_arquivo')
        .eq('id', evidenciaId)
        .single();

      if (fetchError) throw fetchError;

      if (evidencia && (evidencia as any).url_arquivo) {
        // Extrair caminho do arquivo da URL
        const url = new URL((evidencia as any).url_arquivo);
        const filePath = url.pathname.split('/').slice(-2).join('/');
        
        // Deletar arquivo do Storage
        await supabase.storage
          .from('mudancas')
          .remove([filePath]);
      }

      // Deletar registro da tabela
      const { error } = await supabase
        .from('mudanca_evidencias' as any)
        .delete()
        .eq('id', evidenciaId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudanca-evidencias', mudancaId] });
      toast({
        title: "Sucesso",
        description: "Evidência deletada com sucesso!",
      });
    },
    onError: (error) => {
      console.error('Erro ao deletar evidência:', error);
      toast({
        title: "Erro",
        description: "Erro ao deletar evidência. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  return {
    evidencias,
    isLoading,
    uploadEvidencia,
    deleteEvidencia,
  };
};
